import React, { type ReactNode } from 'react';
import type { DrawerProps as AntdDrawerProps } from 'antd';
import { Drawer as AntdDrawer } from 'antd';
import { css } from '@emotion/react';
import { PopupLayerContext, usePopupLayerZIndex } from 'components/common/popup-layer.context';

export type DrawerVariant = 'default' | 'flush' | 'tinted' | 'fullscreen-log';

export type DrawerProps = {
  open?: boolean;
  onClose?: AntdDrawerProps['onClose'];
  title?: ReactNode;
  placement?: AntdDrawerProps['placement'];
  width?: AntdDrawerProps['width'];
  height?: AntdDrawerProps['height'];
  closable?: boolean;
  destroyOnClose?: boolean;
  footer?: ReactNode;
  className?: string;
  /** Replaces the per-site `bodyStyle` / `headerStyle` / `footerStyle` inline styles (D7.4). */
  variant?: DrawerVariant;
  children?: ReactNode;
};

/**
 * antd puts `className` on `.ant-drawer-content`, so these are descendant selectors - two classes
 * against antd's one, which is enough for everything below. Re-probe before adding a property.
 */
const newDesignVariant: Record<DrawerVariant, ReturnType<typeof css> | undefined> = {
  default: undefined,
  flush: css`
    .ant-drawer-body {
      padding: 0;
    }
  `,
  tinted: css`
    .ant-drawer-body {
      padding: 0;
    }

    .ant-drawer-header,
    .ant-drawer-footer {
      background-color: var(--surface-tint-purple);
    }
  `,
  'fullscreen-log': css`
    .ant-drawer-body {
      padding: 0;
      /* A terminal, not a page: this surface stays dark in light mode too. */
      background-color: var(--surface-navigation-navigation-item);
    }
  `
};

// Moved off antd-wrapper's <Global> block (APO-550).
const headerLayout = css`
  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }

  .ant-drawer-close {
    margin-inline-end: 0;
    margin-inline-start: 12px;
  }
`;

const Drawer: React.FC<DrawerProps> = ({ variant = 'default', className, children, ...props }) => {
  // The DS drawer layer, or a step above an enclosing modal; a modal opened from the body nests above it.
  const layerZIndex = usePopupLayerZIndex('drawer');

  return (
    <PopupLayerContext.Provider value={layerZIndex}>
      <AntdDrawer {...props} className={className} zIndex={layerZIndex} css={[headerLayout, newDesignVariant[variant]]}>
        {children}
      </AntdDrawer>
    </PopupLayerContext.Provider>
  );
};

export default Drawer;
